import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ClassResponse,
  EventResponse,
  HorseRegistryResponse,
  MembershipPlanResponse,
  EventClassResponse,
  SponsorResponse,
  EventClassSponsorResponse,
} from '../../../../../../../../../../@shared/models/response.module';
import {
  _AQH_EVE_8,
  _AQH_EVE_17,
  _AQH_EVE_19,
  _AQH_EVE_22,
  AQH_CLS_0,
} from '../../../../../../../../../../@shared/models/input.module';
import { ClassesService } from '../../../../../../../../../../@shared/services/classes-service';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { StaticService } from '../../../../../../../../../../@shared/services/static-service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {addHours} from '../../../../../../../../../../@core/utils/utils.service';
import { AddSponsor } from '../../../manage-event-sponsor/components/add-sponsor/add-sponsor';

@Component({
  selector: 'app-edit-class',
  standalone: false,
  templateUrl: './edit-class.html',
  styleUrl: './edit-class.css'
})
export class EditClass {
  @Input() event!: EventResponse;                 // optional if you always have event_id on the class
  @Input() eventClass!: EventClassResponse;       // the class to edit

  classTypes: ClassResponse[] = [];
  horseRegistries: HorseRegistryResponse[] = [];
  membershipPlans: MembershipPlanResponse[] = [];

  eventClassSponsors: EventClassSponsorResponse[] = [];
  selectedSponsors: SponsorResponse[] = [];

  form: FormGroup;
  saving = false;
  loadingSponsors = false;

  constructor(
    private classesService: ClassesService,
    private eventsService: EventsService,
    private staticService: StaticService,
    private fb: FormBuilder,
    private modal: NgbActiveModal,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      class_name: ['', [Validators.required, Validators.maxLength(200)]],
      class_description: [''],
      parent_class_id: [null],                 // holds a ClassResponse object
      max_num_registrations: [null],
      start_date_time: ['', Validators.required], // "YYYY-MM-DDTHH:mm"
      req_membership_plans: [[]],              // array of MembershipPlanResponse
      req_horse_registration_id: [null],
      max_participant_age: [null],
      min_horse_age: [null],
      time_per_participant_seconds: [null],
      price: [null],
      price_currency: ['NOK'],
    });
  }

  ngOnInit() {
    console.log("Event Class to edit:", this.eventClass);
    // Load selectable data first, then patch form with existing values
    Promise.all([
      this.loadClassTypes(),
      this.loadHorseRegistries(),
      this.loadMembershipPlans(),
    ]).then(() => {
      this.patchFromEventClass();
      this.loadSponsors();
    });
  }

  private loadClassTypes(): Promise<void> {
    const payload: AQH_CLS_0 = {};
    return new Promise((resolve) => {
      this.classesService.getClasses(payload).subscribe(res => {
        this.classTypes = res.data || [];
        resolve();
      });
    });
  }

  private loadHorseRegistries(): Promise<void> {
    return new Promise((resolve) => {
      this.staticService.getHorseRegistries().subscribe(res => {
        this.horseRegistries = res.data || [];
        resolve();
      });
    });
  }

  private loadMembershipPlans(): Promise<void> {
    return new Promise((resolve) => {
      this.staticService.getMembershipPlans().subscribe(res => {
        this.membrshipPlans = res || [];
        resolve();
      });
    });
  }

  // Helper to format into <input type="datetime-local"> expected format
  private toInputLocal(dt: Date | string | null | undefined): string {
    if (!dt) return '';

    // Already correct format
    if (typeof dt === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) {
      return dt;
    }

    // 1) Handle "YYYY-MM-DD HH:mm[:ss]"
    if (typeof dt === 'string') {
      const s = dt.trim();

      const ymd = s.replace('T', ' ').match(
        /^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?$/
      );
      if (ymd) {
        const [, yyyy, mm, dd, hh, mi] = ymd;
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 2) Handle "dd/mm/yyyy, HH:mm"
      const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})$/);
      if (dmy) {
        let [, dd, mm, yyyy, hh, mi] = dmy;
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        hh = hh.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 3) Handle "mm/dd/yyyy, HH:mm" (US ordering)
      const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (mdy) {
        let [, mm, dd, yyyy, hh, mi, ampm] = mdy;
        let hour = parseInt(hh, 10);
        if (ampm) {
          const isPM = ampm.toUpperCase() === 'PM';
          if (isPM && hour < 12) hour += 12;
          if (!isPM && hour === 12) hour = 0;
        }
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        const hh2 = String(hour).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh2}:${mi}`;
      }

      // 4) Last resort: native Date parse
      const nd = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
      if (!isNaN(nd.getTime())) {
        const yyyy = nd.getFullYear();
        const mm = String(nd.getMonth() + 1).padStart(2, '0');
        const dd = String(nd.getDate()).padStart(2, '0');
        const hh = String(nd.getHours()).padStart(2, '0');
        const mi = String(nd.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      return '';
    }

    // Date instance (already local): format without TZ conversion
    const d = dt as Date;
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  private patchFromEventClass() {
    if (!this.eventClass) return;

    // map selected objects for selectors
    const parentClassObj = this.classTypes.find(c => c.class_id === this.eventClass.parent_class_id) || null;
    const selectedPlans =
      (this.eventClass.accepted_membership_plans || [])
        .map(p => this.membrshipPlans.find(mp => mp.plan_id === p.plan_id))
        .filter(Boolean) as MembershipPlanResponse[];

    this.form.patchValue({
      class_name: this.eventClass.class_name || '',
      class_description: this.eventClass.class_description || '',
      parent_class_id: parentClassObj,
      max_num_registrations: this.eventClass.max_num_registrations ?? null,
      start_date_time: this.toInputLocal(this.eventClass.start_date_time as any),
      req_membership_plans: selectedPlans,
      req_horse_registration_id: this.eventClass.req_horse_registration_id ?? null,
      max_participant_age: this.eventClass.max_participant_age ?? null,
      min_horse_age: this.eventClass.min_horse_age ?? null,
      time_per_participant_seconds: this.eventClass.time_per_participant_seconds ?? null,
      price: this.eventClass.price ?? null,
      price_currency: this.eventClass.price_currency || 'NOK',
    });
  }

  v(name: string) {
    return this.form.get(name)?.value;
  }
  set(name: string, value: any) {
    this.form.get(name)?.setValue(value);
    this.form.get(name)?.markAsDirty();
  }

  save() {
    if (this.form.invalid || !this.eventClass?.event_class_id) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;

    // const raw = this.v('start_date_time'); // "YYYY-MM-DDTHH:mm"

    const payload: _AQH_EVE_8 = {
      event_class_id: this.eventClass.event_class_id,
      event_id: this.event?.event_id ?? this.eventClass.event_id,
      parent_class_id: this.v('parent_class_id')?.class_id ?? undefined,
      class_name: this.v('class_name'),
      class_description: this.v('class_description'),
      max_num_registrations: this.v('max_num_registrations') ?? undefined,
      start_date_time: this.v('start_date_time')!,
      req_membership_plan_ids: (this.v('req_membership_plans') || []).map((p: any) => p.plan_id),
      req_horse_registration_id: this.v('req_horse_registration_id') ?? undefined,
      max_participant_age: this.v('max_participant_age') ?? undefined,
      min_horse_age: this.v('min_horse_age') ?? undefined,
      time_per_participant_seconds: this.v('time_per_participant_seconds') ?? undefined,
      price: this.v('price') ?? undefined,
      price_currency: this.v('price_currency') ?? undefined,
    };

    console.log("Payload to save edited event class:", payload);

    this.eventsService.editEventClass(payload).subscribe({
      next: (res) => {
        this.saving = false;
        this.modal.close(true);
      },
      error: () => (this.saving = false),
    });
  }

  // Convenience getters for template
  get classNameInvalid(): boolean {
    const c = this.form.get('class_name');
    return !!(c && c.invalid && c.touched);
  }
  get startInvalid(): boolean {
    const c = this.form.get('start_date_time');
    return !!(c && c.invalid && c.touched);
  }

  getSelectedHorseRegistry() {
    const id = this.v('req_horse_registration_id');
    return this.horseRegistries.find(r => r.registry_id === id) || null;
  }

  loadSponsors() {
    if (!this.eventClass?.event_class_id) return;

    this.loadingSponsors = true;
    this.eventsService.getEventClassSponsors({ event_class_id: this.eventClass.event_class_id }).subscribe({
      next: (res: EventClassSponsorResponse[]) => {
        this.eventClassSponsors = res;
        this.selectedSponsors = res.map((es: EventClassSponsorResponse) => es.sponsor)
          .filter((s): s is SponsorResponse => s !== null && s !== undefined);
        this.loadingSponsors = false;
      },
      error: () => {
        this.loadingSponsors = false;
        this.selectedSponsors = [];
        this.eventClassSponsors = [];
      }
    });
  }

  removeSponsor(sponsor: SponsorResponse) {
    const eventClassSponsor = this.eventClassSponsors.find(es => es.sponsor_id === sponsor.sponsor_id);
    if (!eventClassSponsor) return;

    const payload: _AQH_EVE_19 = {
      entry_id: eventClassSponsor.entry_id
    };

    this.eventsService.removeSponsorFromEventClass(payload).subscribe({
      next: () => {
        this.loadSponsors();
      },
      error: (err) => {
        console.error('Error removing sponsor:', err);
      }
    });
  }

  openCreateSponsorModal() {
    const modalRef = this.modalService.open(AddSponsor, { size: 'lg', centered: true });

    // Ensure we have a valid event object with event_id
    const eventForSponsor = this.event || {
      event_id: this.eventClass?.event_id || 0,
      event_name: '',
      event_description: ''
    } as EventResponse;

    modalRef.componentInstance.event = eventForSponsor;

    modalRef.result.then((result: SponsorResponse) => {
      if (result && result.sponsor_id && this.eventClass?.event_class_id) {
        // Add the newly created sponsor to this event class
        const payload: _AQH_EVE_17 = {
          event_class_id: this.eventClass.event_class_id,
          sponsor_id: result.sponsor_id
        };

        this.eventsService.addSponsorToEventClass(payload).subscribe({
          next: () => {
            // Reload sponsors to show the new one
            this.loadSponsors();
          },
          error: (err) => {
            console.error('Error adding sponsor to event class:', err);
          }
        });
      }
    }, () => {});
  }

  close() {
    this.modal.close()
  }

  // local alias to avoid typos
  private get membrshipPlans() { return this.membershipPlans; }
  private set membrshipPlans(v: MembershipPlanResponse[]) { this.membershipPlans = v; }
}
